import React from "react";
import RenderIfVisible from "react-render-if-visible";
import { list, OptimizedGantt, ProjectElementType } from "@planzone/dto";
import { sort as sortData } from "@common/sort";
import { filter as filterData } from "@common/filter";
import { DragAndDrop, Draggable } from "@common/dragAndDrop";
import { QuickCreationMenuWrapper } from "@common/quickCreationMenuWrapper";
import { ElementCreationForm, ElementTypes } from "@common/elementCreationForm";
import { ActivityWatcher } from "@common/activityWatcher";
import { useUtils } from "@common/treeToggle";
import { Props } from "./types";
import { Loader } from "./loader";
import { ProjectLoader } from "./projectLoader";
import { QuickElementLoader } from "./quickElementLoader";
import { Project } from "./project";
import { List } from "./list";
import { Task } from "./task";
import { Milestone } from "./milestone";
import { Event } from "./event";
import { Preview } from "./preview";
import { Context } from "./context";
import { DropBox } from "./dropBox";
import { ProjectCtx } from "./projectCtx";
import { Placeholder } from "./placeholder";
import { renderWithTreeCtx } from "./utils";
import styles from "./styles.scss";

import { useHoverActions } from "@common/elementHover";

const DEFAULT_ESTIMATED_HEIGHT = 40;

function canAddAfter(
  gantt: OptimizedGantt,
  list: OptimizedGantt[number],
  breadcrumbs: OptimizedGantt,
  index: number,
): boolean {
  const currentElement = gantt[index];
  const nextElement = gantt[index + 1];
  const isLastElement = !nextElement;
  const isAtListEnd =
    !isLastElement &&
    nextElement.depth <= list.depth &&
    !!breadcrumbs[breadcrumbs.length - 1] &&
    breadcrumbs[breadcrumbs.length - 1].uuid === list.uuid;
  const isNextToTarget =
    !!nextElement &&
    currentElement.uuid === list.uuid &&
    currentElement.depth >= nextElement.depth;

  return (
    isNextToTarget ||
    (!isLastElement
      ? isAtListEnd ||
        (!!breadcrumbs.find(({ uuid }) => uuid === list.uuid) &&
          nextElement.depth <= list.depth)
      : !!breadcrumbs.find(({ uuid }) => uuid === list.uuid) ||
        list.uuid === gantt[index].uuid)
  );
}

function Wbs({
  projects: p,
  filter,
  sort,
  style = {},
  meta = {},
  configuration,
  loaderId = "wbs",
  hideProjects = false,
  isFetchingProjects,
}: Props) {
  const { hoverElement, leaveElement } = useHoverActions();
  const projects = sortData(
    sort,
    filterData(p, filter, (project) => ({ project })),
    (project) => ({
      name: project.name,
      startDate: project.rootList.startDate,
      endDate: project.rootList.endDate,
    }),
  );
  const { isOpen } = useUtils();
  const [openedCreationMenuMap, setOpenedCreationMenuMap] = React.useState<{
    [key: string]: OptimizedGantt[number];
  }>({});
  const [isDragging, setIsDragging] = React.useState(false);

  const [addInElement, setAddInElement] = React.useState<
    null | OptimizedGantt[number]
  >(null);
  const [addAfterAtElement, setAddAfterElement] = React.useState<
    null | OptimizedGantt[number]
  >(null);
  const inRef = React.useRef<HTMLDivElement>(null);
  const afterRef = React.useRef<HTMLDivElement>(null);
  const indentSize =
    style && style.indentSize
      ? typeof style.indentSize === "number"
        ? `${style.indentSize}px`
        : style.indentSize
      : `3.5rem`;

  ActivityWatcher.useActivityWatcher(inRef, {
    blur: () => setAddInElement(null),
  });

  ActivityWatcher.useActivityWatcher(afterRef, {
    blur: () => setAddAfterElement(null),
  });

  return (
    <Context.Provider
      value={{
        configuration,
        style: {
          ...style,
          itemHeight: style.itemHeight || DEFAULT_ESTIMATED_HEIGHT,
        },
      }}
    >
      <DragAndDrop>
        <div className={styles.wbs}>
          {projects.map((project) => (
            <ProjectCtx
              key={project.uuid}
              project={project}
              filter={filter}
              sort={sort}
              isMultiProject={projects.length > 1}
              hideProjects={hideProjects}
            >
              {(gantt, isFetching) => (
                <>
                  {!!project && (
                    <DropBox
                      type="dropStart"
                      gantt={gantt}
                      parentList={project.rootList}
                    />
                  )}
                  {renderWithTreeCtx(
                    gantt,
                    project,
                    isOpen,
                    (projectElement, ctx, index, _, breadcrumbs) => {
                      const isList =
                        projectElement.type === ProjectElementType.LIST;
                      const isListEnd = !!gantt[index + 1]
                        ? gantt[index + 1].depth < projectElement.depth
                        : true;
                      const parentList =
                        projectElement.parentUuid && projectElement.depth > 0
                          ? {
                              ...list,
                              uuid: projectElement.parentUuid,
                            }
                          : {
                              ...list,
                              uuid:
                                project?.rootList.uuid ||
                                projectElement.activityRootUuid,
                            };
                      const elMeta = meta[projectElement.uuid] || {
                        uuid: projectElement.uuid,
                        allocatedHours: 0,
                        attachmentCount: 0,
                        commentCount: 0,
                        elementCount: 0,
                      };

                      return (
                        <div
                          key={projectElement.uuid}
                          onMouseEnter={() => hoverElement(projectElement)}
                          onMouseLeave={() => leaveElement(projectElement)}
                        >
                          <div
                            style={{
                              marginLeft: `calc(${projectElement.depth} * ${indentSize})`,
                              height:
                                (style.itemHeight || DEFAULT_ESTIMATED_HEIGHT) *
                                (openedCreationMenuMap[projectElement.uuid]
                                  ? 2
                                  : 1),
                            }}
                          >
                            <RenderIfVisible
                              root={document.getElementById(loaderId)}
                              defaultHeight={
                                style.itemHeight || DEFAULT_ESTIMATED_HEIGHT
                              }
                              placeholderElement="div"
                              placeholderElementClass={styles.placeholder}
                              stayRendered
                            >
                              <DropBox
                                type={isList ? "dropInList" : "dropAtEl"}
                                style={{
                                  height:
                                    style.itemHeight ||
                                    DEFAULT_ESTIMATED_HEIGHT,
                                }}
                                parentList={
                                  isList
                                    ? {
                                        ...list,
                                        uuid: projectElement.uuid,
                                      }
                                    : parentList
                                }
                                gantt={gantt}
                                projectElement={projectElement}
                                index={index}
                              >
                                {(_, canDrop) => (
                                  <QuickCreationMenuWrapper
                                    project={project}
                                    parentList={
                                      isList
                                        ? {
                                            ...list,
                                            uuid: projectElement.uuid,
                                          }
                                        : parentList
                                    }
                                    isInactive={isDragging}
                                    range={{
                                      min: isList
                                        ? null
                                        : {
                                            ...projectElement,
                                            rank: projectElement.referenceKeyRank,
                                          },
                                      max: !!gantt[index + 1]
                                        ? {
                                            ...gantt[index + 1],
                                            rank: gantt[index + 1]
                                              .referenceKeyRank,
                                          }
                                        : null,
                                    }}
                                    loader={(type, name) => (
                                      <div
                                        className={styles.elementLoaderWrapper}
                                      >
                                        <QuickElementLoader
                                          type={type}
                                          name={name}
                                          parentList={parentList}
                                        />
                                      </div>
                                    )}
                                    isThick={isList}
                                    toggle={(isOpen) => {
                                      setOpenedCreationMenuMap((m) => {
                                        if (isOpen) {
                                          return {
                                            ...m,
                                            [projectElement.uuid]:
                                              projectElement,
                                          };
                                        }

                                        return Object.fromEntries(
                                          Object.entries(m).filter(
                                            ([key]) =>
                                              key !== projectElement.uuid,
                                          ),
                                        );
                                      });
                                    }}
                                  >
                                    <Draggable
                                      className={styles.draggable}
                                      type={
                                        isList
                                          ? "MOVE_LIST"
                                          : projectElement.type ===
                                              ProjectElementType.TASK
                                            ? "MOVE_TASK"
                                            : projectElement.type ===
                                                ProjectElementType.MILESTONE
                                              ? "MOVE_MILESTONE"
                                              : "MOVE_EVENT"
                                      }
                                      data={{
                                        projectElement,
                                        parentList,
                                      }}
                                      startDrag={() => setIsDragging(true)}
                                      stopDrag={() => setIsDragging(false)}
                                    >
                                      {projectElement.type ===
                                      ProjectElementType.PROJECT ? (
                                        <Project
                                          project={projectElement}
                                          meta={elMeta}
                                          filter={filter}
                                          isMultiProject={projects.length > 1}
                                        />
                                      ) : projectElement.type ===
                                        ProjectElementType.TASK ? (
                                        <Task
                                          task={projectElement}
                                          meta={elMeta}
                                          parentList={parentList}
                                          isMultiProject={projects.length > 1}
                                          minDate={
                                            breadcrumbs[breadcrumbs.length - 1]
                                              ?.startDate || ctx.minDate
                                          }
                                          maxDate={ctx.maxDate}
                                        />
                                      ) : isList ? (
                                        <List
                                          list={projectElement}
                                          meta={elMeta}
                                          parentList={parentList}
                                          isMultiProject={projects.length > 1}
                                          minDate={ctx.minDate}
                                          maxDate={ctx.maxDate}
                                          addIn={() =>
                                            setAddInElement(projectElement)
                                          }
                                          addAfter={() =>
                                            setAddAfterElement(projectElement)
                                          }
                                        />
                                      ) : projectElement.type ===
                                        ProjectElementType.MILESTONE ? (
                                        <Milestone
                                          milestone={projectElement}
                                          meta={elMeta}
                                          parentList={parentList}
                                          isMultiProject={projects.length > 1}
                                          minDate={
                                            breadcrumbs[breadcrumbs.length - 1]
                                              ?.startDate || ctx.minDate
                                          }
                                          maxDate={ctx.maxDate}
                                        />
                                      ) : projectElement.type ===
                                        ProjectElementType.EVENT ? (
                                        <Event
                                          event={projectElement}
                                          meta={elMeta}
                                          parentList={parentList}
                                          isMultiProject={projects.length > 1}
                                          minDate={
                                            breadcrumbs[breadcrumbs.length - 1]
                                              ?.startDate || ctx.minDate
                                          }
                                          maxDate={ctx.maxDate}
                                        />
                                      ) : (
                                        <></>
                                      )}
                                    </Draggable>
                                    <Preview
                                      canDrop={canDrop}
                                      isList={isList}
                                    />
                                  </QuickCreationMenuWrapper>
                                )}
                              </DropBox>
                            </RenderIfVisible>
                          </div>
                          {isListEnd &&
                            new Array(breadcrumbs.length)
                              .fill(0)
                              .map((_, i, arr) => {
                                const minEl =
                                  breadcrumbs[
                                    breadcrumbs.length - (arr.length - i)
                                  ];

                                return (
                                  <DropBox
                                    type="dropAfterList"
                                    gantt={gantt}
                                    nextParent={
                                      minEl.parentUuid === project.uuid
                                        ? project.rootList
                                        : {
                                            ...list,
                                            ...minEl,
                                            uuid: minEl.parentUuid,
                                          }
                                    }
                                    minEl={minEl}
                                    maxEl={
                                      gantt[index + 1] &&
                                      gantt[index + 1].depth === minEl.depth
                                        ? gantt[index + 1]
                                        : null
                                    }
                                    index={i}
                                    style={
                                      typeof style.indentSize === "number"
                                        ? {
                                            marginLeft: `${style.indentSize * (breadcrumbs.length - (arr.length - i))}px`,
                                          }
                                        : {
                                            marginLeft: `${3.5 * (breadcrumbs.length - (arr.length - i))}rem`,
                                          }
                                    }
                                  />
                                );
                              })}
                          {isList &&
                            ((gantt[index + 1]
                              ? gantt[index + 1].depth <= projectElement.depth
                              : true) ||
                              !isOpen(projectElement.uuid)) && (
                              <DropBox
                                type="dropAfterList"
                                gantt={gantt}
                                nextParent={parentList}
                                minEl={projectElement}
                                maxEl={
                                  !isOpen(projectElement.uuid)
                                    ? gantt
                                        .slice(index + 1)
                                        .find(
                                          (el) =>
                                            el.uuid !== projectElement.uuid &&
                                            el.depth === projectElement.depth,
                                        ) || null
                                    : gantt[index + 1] || null
                                }
                                index={0}
                                style={
                                  typeof style.indentSize === "number"
                                    ? {
                                        marginLeft: `${style.indentSize * projectElement.depth}px`,
                                      }
                                    : {
                                        marginLeft: `${3.5 * projectElement.depth}rem`,
                                      }
                                }
                              />
                            )}
                          {addInElement &&
                            addInElement.uuid === projectElement.uuid && (
                              <div
                                ref={inRef}
                                className={styles.elementCreationForm}
                                style={{
                                  marginLeft: `calc(${projectElement.depth + 1} * ${indentSize})`,
                                  height:
                                    style.itemHeight ||
                                    DEFAULT_ESTIMATED_HEIGHT,
                                }}
                              >
                                <ElementCreationForm
                                  project={project}
                                  parentList={{
                                    ...list,
                                    ...projectElement,
                                  }}
                                  range={{
                                    min: null,
                                    max: null,
                                  }}
                                  loader={(type, name) => (
                                    <QuickElementLoader
                                      type={type}
                                      name={name}
                                      parentList={{
                                        ...list,
                                        ...projectElement,
                                      }}
                                    />
                                  )}
                                  done={() => setAddInElement(null)}
                                />
                              </div>
                            )}
                          {addAfterAtElement &&
                            canAddAfter(
                              gantt,
                              addAfterAtElement,
                              breadcrumbs,
                              index,
                            ) && (
                              <div
                                ref={afterRef}
                                className={styles.elementCreationForm}
                                style={{
                                  marginLeft:
                                    typeof style.indentSize === "number"
                                      ? `${style.indentSize * addAfterAtElement.depth}px`
                                      : `${3.5 * addAfterAtElement.depth}rem`,
                                  minHeight:
                                    style.itemHeight ||
                                    DEFAULT_ESTIMATED_HEIGHT,
                                  height: 0,
                                }}
                              >
                                <ElementCreationForm
                                  project={project}
                                  parentList={
                                    addAfterAtElement.depth === 0
                                      ? project.rootList
                                      : {
                                          ...list,
                                          uuid: addAfterAtElement.parentUuid,
                                        }
                                  }
                                  range={{
                                    min: addAfterAtElement
                                      ? {
                                          ...addAfterAtElement,
                                          rank: addAfterAtElement.referenceKeyRank,
                                        }
                                      : null,
                                    max:
                                      gantt[index + 1] &&
                                      gantt[index + 1].depth ===
                                        addAfterAtElement.depth
                                        ? {
                                            ...gantt[index + 1],
                                            rank: gantt[index + 1]
                                              .referenceKeyRank,
                                          }
                                        : null,
                                  }}
                                  loader={(type, name) => (
                                    <QuickElementLoader
                                      type={type}
                                      name={name}
                                      parentList={{
                                        ...list,
                                        ...projectElement,
                                      }}
                                    />
                                  )}
                                  defaultType={ElementTypes.LIST}
                                  done={() => setAddAfterElement(null)}
                                />
                              </div>
                            )}
                        </div>
                      );
                    },
                  )}
                  {!isFetching &&
                    p.length === 1 &&
                    gantt.filter(
                      ({ type }) => type !== ProjectElementType.PROJECT,
                    ).length === 0 && (
                      <Placeholder project={project} filter={filter} />
                    )}
                  {isFetching && <Loader style={style} />}
                </>
              )}
            </ProjectCtx>
          ))}
          {isFetchingProjects && <ProjectLoader style={style} />}
        </div>
      </DragAndDrop>
    </Context.Provider>
  );
}

export { Wbs };
